import type { FormEvent } from 'react'
import type { Contact } from '@/constants/crm'

import { useState } from 'react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import FormSection from '@/components/companies/new-company/form-section'
import { Input } from '@/components/ui/input'
import { CompanySelect, OwnerSelect } from '@/components/generals/form-selects'
import { OWNERS } from '@/constants/companies'
import { apiRequest } from '@/lib/client-api'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

type FormState = {
    name: string
    title: string
    email: string
    phone: string
    companyId: string
    owner: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function ContactForm() {
    const defaults = useCrmStore(state => state.dialogDefaults)
    const companies = useCrmStore(state => state.companies)
    const upsertContact = useCrmStore(state => state.upsertContact)
    const closeDialog = useCrmStore(state => state.closeDialog)
    const openContact = useCrmStore(state => state.openContact)
    const showNotice = useCrmStore(state => state.showNotice)
    const companyOwner = companies.find(company => company.id === defaults.companyId)?.owner
    const [form, setForm] = useState<FormState>({
        name: '',
        title: '',
        email: '',
        phone: '',
        companyId: defaults.companyId ?? '',
        owner: companyOwner && OWNERS.some(owner => owner.name === companyOwner) ? companyOwner : OWNERS[0].name
    })
    const [errors, setErrors] = useState<{ name?: string, email?: string }>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm(current => ({ ...current, [key]: value }))
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        const email = form.email.trim()
        const nextErrors = {
            name: form.name.trim() ? undefined : "Enter the contact's name.",
            email: !email || EMAIL_PATTERN.test(email) ? undefined : "Enter a valid email address, like name@company.com."
        }

        setErrors(nextErrors)

        if (nextErrors.name || nextErrors.email) return

        setSubmitting(true)
        setFormError(null)

        const result = await apiRequest<{ contact: Contact }>('/api/contacts', 'POST', {
            ...form,
            name: form.name.trim(),
            title: form.title.trim(),
            email,
            phone: form.phone.trim()
        })

        setSubmitting(false)

        if (!result.ok) {
            setFormError(result.message)

            return
        }

        upsertContact(result.data.contact)
        closeDialog()
        openContact(result.data.contact.id)
        showNotice('success', "Contact added.")
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col' aria-busy={submitting}>
            <DialogHeader>
                <DialogTitle>New Contact</DialogTitle>
                <DialogDescription>
                    Add a person you work with at one of your accounts.
                </DialogDescription>
            </DialogHeader>

            <FormSection title="Person">
                <Field label="Full name" htmlFor='contact-name' required error={errors.name}>
                    <Input
                        id='contact-name'
                        value={form.name}
                        onChange={event => {
                            update('name', event.target.value)
                            if (errors.name) setErrors(current => ({ ...current, name: undefined }))
                        }}
                        placeholder="Jordan Lee"
                        maxLength={120}
                        autoComplete='off'
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        className='aria-invalid:border-danger'
                        autoFocus
                    />
                </Field>
                <Field label="Job title" htmlFor='contact-title'>
                    <Input
                        id='contact-title'
                        value={form.title}
                        onChange={event => update('title', event.target.value)}
                        placeholder="Head of Procurement"
                        maxLength={120}
                        autoComplete='off'
                    />
                </Field>
                <div className='grid gap-4 sm:grid-cols-2'>
                    <Field label="Email" htmlFor='contact-email' error={errors.email}>
                        <Input
                            id='contact-email'
                            type='email'
                            value={form.email}
                            onChange={event => {
                                update('email', event.target.value)
                                if (errors.email) setErrors(current => ({ ...current, email: undefined }))
                            }}
                            placeholder="jordan@company.com"
                            maxLength={200}
                            autoComplete='off'
                            aria-invalid={errors.email ? true : undefined}
                            aria-describedby={errors.email ? 'contact-email-error' : undefined}
                            className='aria-invalid:border-danger'
                        />
                    </Field>
                    <Field label="Phone" htmlFor='contact-phone'>
                        <Input
                            id='contact-phone'
                            type='tel'
                            value={form.phone}
                            onChange={event => update('phone', event.target.value)}
                            placeholder="+1 (415) 555-0100"
                            maxLength={40}
                            autoComplete='off'
                        />
                    </Field>
                </div>
            </FormSection>

            <FormSection title="Account">
                <Field label="Company" htmlFor='contact-company'>
                    <CompanySelect id='contact-company' value={form.companyId} onChange={value => update('companyId', value)} />
                </Field>
                <Field label="Account owner" htmlFor='contact-owner'>
                    <OwnerSelect id='contact-owner' value={form.owner} onChange={value => update('owner', value)} />
                </Field>
            </FormSection>

            {formError && (
                <div role='alert' className='caption-style text-danger px-6 pb-4'>
                    {formError}
                </div>
            )}

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant='subtle' size='sm'>
                        Cancel
                    </Button>
                </DialogClose>
                <Button variant='primary' size='sm' type='submit' disabled={submitting}>
                    <PlusIcon aria-hidden className='size-3' />
                    {submitting ? "Saving…" : "Create Contact"}
                </Button>
            </DialogFooter>
        </form>
    )
}

export default function ContactDialog() {
    const open = useCrmStore(state => state.dialog === 'contact')
    const closeDialog = useCrmStore(state => state.closeDialog)

    return (
        <Dialog open={open} onOpenChange={next => !next && closeDialog()}>
            <DialogContent className='max-w-[520px]'>
                <ContactForm />
            </DialogContent>
        </Dialog>
    )
}
